import { useContext, useEffect, useMemo, useState } from 'react';
import { baseUrl, highStakes, optIds, seams } from '../constants';
import { IOnePaymentItem, IOneTimePaymentItem, IPlan, ISubscription, ItemTypeEnum } from '../model';
import { useProcessPlans } from './useProcessPlans.hook';
import { adapter } from '../utils/adapter';
import AuthContext from '../context/Auth.context';

export const useGetPlans = (
  subscriptions: ISubscription[],
  sport: string,
  isShowAll: boolean,
  yearly?: boolean
) => {
  const [plans, setPlans] = useState<IPlan[]>([]);
  const [onePaymentItems, setOnePaymentItems] = useState<IOneTimePaymentItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const contextData = useContext(AuthContext);

  // Get One time items
  useEffect(() => {
    // @ts-ignore
    fetch(`${baseUrl}/subscriptions/one-time-payment/items`)
      .then(res => res.json())
      .then((response: IOnePaymentItem[]) => {
        const items = response.filter((item) => optIds.includes(item.id));

        const processed = items.map(item => {
          const current = contextData?.boughtOnePaymentItems.find(bought => bought.item.id === item.id)
          return {
            ...item,
            current
          }
        })
        setOnePaymentItems(processed.map(adapter));
      })
  }, []);


  useEffect(() => {
    // @ts-ignore
    fetch(`${baseUrl}/plans/`)
      .then(res => res.json())
      .then((response) => {
        setPlans(response.map((plan: IPlan) => {
          plan.type = ItemTypeEnum.plan;
          if (highStakes.includes(plan.plan_id) || seams.includes(plan.plan_id)) {
            return {
              ...plan,
              display: {
                ...plan.display,
                sport_category: 'Specials'
              },
              isAdditional: false,
              type: ItemTypeEnum.plan
            };
          }
          if (plan.display.sport_category === 'Stats') {
            return {
              ...plan,
              prices: {
                yearly: plan.prices.yearly
              }
            };
          }
          return {
            ...plan,
            isAdditional: false,
            type: ItemTypeEnum.plan
          }
        }));
      }).then(() => {
      setLoading(false)
    });
  }, []);

  const {
    plans: processedPlans,
    totalPrice,
    currentSubscriptions
  } = useProcessPlans(plans, subscriptions);

  return useMemo(() => {
    const allPlans = [...processedPlans, ...(window.customPlans?.map(plan => ({
      ...plan,
      type: ItemTypeEnum.custom
    })) || [])];
    const hasSubscriptions = Object.keys(currentSubscriptions).length;
    const checkedDisabledPlans = allPlans.filter(plan => {
      if (!isShowAll && hasSubscriptions) return !plan.disabled;
      return true;
    })

    const withoutDisabledPlansFiltered = checkedDisabledPlans.filter(plan => plan.display.sport_category === sport)
    const hasMonthly = withoutDisabledPlansFiltered.some(plan => plan.prices?.monthly);
    const hasYearly = withoutDisabledPlansFiltered.some(plan => plan.prices?.yearly);
    const showTimePeriodSwitcher = hasMonthly && hasYearly;
    const yearlyProcessed = showTimePeriodSwitcher ? yearly : hasYearly;

    return {
      loading,
      allPlans,
      yearly: Boolean(yearlyProcessed),
      showTimePeriodSwitcher,
      withoutDisabledPlans: checkedDisabledPlans,
      plans: withoutDisabledPlansFiltered,
      precessedOnePaymentItems: onePaymentItems,
      onePaymentItems: onePaymentItems.filter(plan => plan.display.sport_category === sport),
      currentSubscriptions,
      totalPrice
    };
  }, [processedPlans, onePaymentItems, currentSubscriptions, sport, isShowAll, yearly, totalPrice])
};
